import { observer } from "mobx-react-lite";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "ui";

import { getSectionTypeLabel } from "src/components/SectionInlineFields";
import { SectionFormatIcon } from "src/workouts/SectionFormatIcon";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";

import { MinimapItemBase } from "./MinimapItemBase";

export interface MinimapSectionItemProps {
  id: string;
  level: number;
  index: number;
  isLast: boolean;
  childCount: number;
  previousItemId?: string;
  children: ReactNode;
}

export const BaseMinimapSectionItem = observer(function BaseMinimapSectionItem(
  props: MinimapSectionItemProps & { badges?: ReactNode },
) {
  const { t } = useTranslation();
  const workoutEditStore = useWorkoutEditStore();
  const workoutItem = workoutEditStore.getGroupWorkoutItemById(props.id);

  const displayName =
    workoutEditStore.getWorkoutItemName(workoutItem.id) ||
    t("editWorkout.defaultGroupName");

  return (
    <MinimapItemBase
      id={props.id}
      level={props.level}
      index={props.index}
      previousItemId={props.previousItemId}
      displayName={displayName}
      className={cn({
        "pt-2": props.index === 0 && !!workoutItem.parentId,
      })}
      nestedItems={props.children}
    >
      <span className="shrink-0">
        <SectionFormatIcon format={workoutItem.format} />
      </span>
      <span className="text-xs truncate flex-1 min-w-0 font-semibold text-slate-700">
        {displayName}
      </span>
      {workoutItem.groupType && workoutItem.groupType !== "SUPER_SET" ? (
        <span className="text-[10px] px-1 rounded shrink-0 text-slate-500">
          {getSectionTypeLabel(workoutItem.groupType, t)}
        </span>
      ) : null}
      {props.badges}
      {props.childCount > 0 ? (
        <span className="text-[10px] px-1 py-0 rounded shrink-0 bg-slate-200 text-slate-600">
          {props.childCount}
        </span>
      ) : null}
    </MinimapItemBase>
  );
});
